import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from '@/components/ui/calendar';
import { addDays, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { CalendarDays } from 'lucide-react';

interface StepDateProps {
  restaurantId: string;
  value: Date | null;
  onSelect: (date: Date) => void;
  onAutoAdvance: () => void;
}

const StepDate: React.FC<StepDateProps> = ({ restaurantId, value, onSelect, onAutoAdvance }) => {
  const [maxDays, setMaxDays] = useState(30);
  const [minHours, setMinHours] = useState(0);
  const [closedDays, setClosedDays] = useState<number[]>([]);

  useEffect(() => {
    const fetchSettings = async () => {
      const [{ data: settings }, { data: serviceTimes }] = await Promise.all([
        supabase.from('reservation_settings').select('max_reservation_days, min_reservation_hours').eq('restaurant_id', restaurantId).maybeSingle(),
        supabase.from('service_times').select('day_of_week, lunch_start, dinner_start').eq('restaurant_id', restaurantId),
      ]);
      if (settings) {
        setMaxDays(settings.max_reservation_days || 30);
        setMinHours(settings.min_reservation_hours ?? 0);
      }
      // Days with no service times at all are "closed"
      if (serviceTimes && serviceTimes.length > 0) {
        const activeDays = new Set(serviceTimes
          .filter(st => st.lunch_start || st.dinner_start)
          .map(st => st.day_of_week));
        const closed: number[] = [];
        for (let d = 0; d < 7; d++) {
          if (!activeDays.has(d)) closed.push(d);
        }
        setClosedDays(closed);
      }
    };
    fetchSettings();
  }, [restaurantId]);

  const today = startOfDay(new Date());
  const minDate = minHours > 0
    ? startOfDay(new Date(Date.now() + minHours * 3600000))
    : today;
  const maxDate = addDays(today, maxDays);

  const disabledDays = (date: Date) => {
    if (isBefore(date, minDate)) return true;
    if (date > maxDate) return true;
    if (closedDays.includes(date.getDay())) return true;
    return false;
  };

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    onSelect(date);
    setTimeout(() => onAutoAdvance(), 300);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col items-center gap-4 py-2"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <CalendarDays className="h-5 w-5" />
        <span className="text-sm font-medium">Choisissez une date</span>
      </div>
      <Calendar
        mode="single"
        selected={value || undefined}
        onSelect={handleSelect}
        disabled={disabledDays}
        locale={fr}
        className="rounded-xl border shadow-sm"
        classNames={{
          day_selected: 'bg-primary text-primary-foreground font-bold',
          day_today: 'border-2 border-primary/40',
        }}
      />
    </motion.div>
  );
};

export default StepDate;
